
import { useState, useEffect, useRef } from 'react';
import { FastingProtocol } from '@/types/fasting';
import { fastingProtocols } from '@/data/fastingProtocols';
import { loadFastingState } from './loadFastingState';
import { syncFastingState } from './syncFastingState';
import { calculateElapsedTime } from './calculateElapsedTime';

export const useFastingPersistence = () => {
  // Initialize state with defaults
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [selectedProtocol, setSelectedProtocol] = useState(fastingProtocols[0]);
  const [waterIntake, setWaterIntake] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [pausedTime, setPausedTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [totalPauseDuration, setTotalPauseDuration] = useState<number>(0);
  
  // Track initialization
  const isInitialized = useRef(false);
  const hasLoadedState = useRef(false);

  // Load state immediately on mount - this is critical for persistence
  useEffect(() => {
    if (isInitialized.current) return;
    
    console.log("[Persistence] Initializing fasting state persistence...");
    isInitialized.current = true;
    
    // Load state synchronously
    const loadedState = loadFastingState(fastingProtocols);
    
    if (loadedState) {
      console.log("[Persistence] Applying loaded state:", {
        isActive: loadedState.isActive,
        hasStartTime: !!loadedState.startTime,
        timeElapsed: loadedState.timeElapsed,
        protocol: loadedState.selectedProtocol?.name
      });
      
      // Apply all state immediately in a single batch
      setIsActive(loadedState.isActive);
      setIsPaused(loadedState.isPaused || false);
      setTimeElapsed(loadedState.timeElapsed || 0);
      
      if (loadedState.selectedProtocol) {
        setSelectedProtocol(loadedState.selectedProtocol);
      } else if (loadedState.selectedProtocolId) {
        const protocol = fastingProtocols.find(p => p.id === loadedState.selectedProtocolId);
        if (protocol) setSelectedProtocol(protocol);
      }
      
      setWaterIntake(loadedState.waterIntake || 0);
      setStartTime(loadedState.startTime);
      setPausedTime(loadedState.pausedTime || null);
      setEndTime(loadedState.endTime || null);
      setTotalPauseDuration(loadedState.totalPauseDuration || 0);

      // Recalculate elapsed time for active, non-paused timers
      if (loadedState.isActive && !loadedState.isPaused && loadedState.startTime) {
        const currentElapsed = calculateElapsedTime(
          loadedState.startTime,
          false,
          loadedState.totalPauseDuration || 0
        );
        
        console.log("[Persistence] Updating elapsed time for active timer:", {
          from: loadedState.timeElapsed,
          to: currentElapsed,
          startTime: new Date(loadedState.startTime).toLocaleString()
        });
        
        setTimeElapsed(currentElapsed);
      }
      
      console.log("[Persistence] State restoration complete");
    } else {
      console.log("[Persistence] No saved state found, using defaults");
    }
  }, []);
  
  // Set hasLoadedState flag after state has been applied to prevent race conditions
  useEffect(() => {
    if (isInitialized.current && !hasLoadedState.current) {
      hasLoadedState.current = true;
      console.log("[Persistence] State loading complete, enabling sync");
    }
  }, [isActive, isPaused, timeElapsed, selectedProtocol, startTime]);
  
  // Save state whenever it changes (but only after initial load)
  // Throttle timeElapsed changes to avoid saving every second
  useEffect(() => {
    if (!hasLoadedState.current) return;
    
    console.log("[Persistence] State changed, syncing:", {
      isActive,
      hasStartTime: !!startTime,
      timeElapsed,
      protocol: selectedProtocol.name
    });
    
    syncFastingState({
      isActive,
      isPaused,
      timeElapsed,
      selectedProtocol,
      waterIntake,
      startTime,
      pausedTime,
      endTime,
      totalPauseDuration
    });
  }, [isActive, isPaused, selectedProtocol, waterIntake, startTime, pausedTime, endTime, totalPauseDuration]);

  // Save timeElapsed separately with throttling to avoid constant saves
  useEffect(() => {
    if (!hasLoadedState.current) return;
    
    const saveTimeElapsed = setTimeout(() => {
      syncFastingState({
        isActive,
        isPaused,
        timeElapsed,
        selectedProtocol,
        waterIntake,
        startTime,
        pausedTime,
        endTime,
        totalPauseDuration
      });
    }, 5000); // Save every 5 seconds instead of every second
    
    return () => clearTimeout(saveTimeElapsed);
  }, [timeElapsed]);
  
  return {
    isActive,
    setIsActive,
    isPaused,
    setIsPaused,
    timeElapsed,
    setTimeElapsed,
    selectedProtocol,
    setSelectedProtocol,
    waterIntake,
    setWaterIntake,
    startTime,
    setStartTime,
    pausedTime,
    setPausedTime,
    endTime,
    setEndTime,
    totalPauseDuration,
    setTotalPauseDuration,
    isLoaded: hasLoadedState.current
  };
};
